import { recordsPerPage } from "@/lib/pos/constants";
import type { GridProps } from "@/types/pos";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from "lucide-react";

export default function CommonGrid({ rowData, isPaginationRequired = true, pagination, columns = [], isLoading, sortColumn, sortDirection, onSort }: GridProps) {
	const totalPages = pagination?.totalPages;
	const currentPage = pagination?.currentPage;
	const itemsPerPage = pagination?.itemsPerPage;
	const totalItems = pagination?.totalItems;
	const handlePageChange = pagination?.handlePageChange;
	const handleItemsPerPageChange = pagination?.handleItemsPerPageChange;

	const itemsPerPageNum = Number(itemsPerPage || recordsPerPage.defaultSize) || recordsPerPage.defaultSize;
	const currentPageNum = Math.max(1, Number(currentPage ?? 1) || 1);
	const totalItemsNum = Math.max(0, Number(totalItems ?? 0) || 0);
	const totalPagesNum = Number((totalPages ?? Math.ceil(totalItemsNum / itemsPerPageNum)) ?? 0) ?? 0;
	const start = totalItemsNum === 0 ? 0 : (currentPageNum - 1) * itemsPerPageNum + 1;
	const end = totalItemsNum === 0 ? 0 : Math.min(currentPageNum * itemsPerPageNum, totalItemsNum);

	return (
		<div className="order-wrapper">
			<div className="border border-[var(--layout-border-color)] rounded-[4px] overflow-x-auto">
				<table className="w-full bg-[var(--primary-background-color)] rounded-[4px] min-w-max whitespace-nowrap overflow-hidden">
					<thead>
						<tr className="border-b bg-[var(--secondary-background-color)] rounded-[4px] border-[var(--ui-border-color)] h-[38px]">
							{columns?.filter(col => !col?.hidden)?.map((column, i) => {
								const isSortable = column.sortable && onSort;
								const isActive = sortColumn === column.field;

								return (
									<th
										key={i}
										className={`px-4 text-left font-medium text-[13px] leading-[0] text-[var(--primary-text-color)] ${isSortable ? 'cursor-pointer select-none hover:bg-[var(--primary-background-hover-color)]' : ''}`}
										onClick={() => isSortable && onSort(column.field)}
									>
										<div className="flex items-center gap-1">
											{column.headerTemplate ? column.headerTemplate() : column.label}
											{isSortable && (
												<span className="ml-1">
													{isActive ? (
														sortDirection === "asc" ? (
															<ArrowUp className="w-3 h-3" />
														) : (
															<ArrowDown className="w-3 h-3" />
														)
													) : (
														<ArrowUpDown className="w-3 h-3 opacity-40" />
													)}
												</span>
											)}
										</div>
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							[...Array(10)].map((_, rowIndex) => (
								<tr key={rowIndex} className="animate-pulse">
									{columns.map((_, colIndex) => (
										<td key={colIndex} className="p-4">
											<div className="bg-gray-200 h-6 rounded w-full" />
										</td>
									))}
								</tr>
							))
						) : (
							rowData?.map((row, rowIndex) => (
								<tr
									key={rowIndex}
									className="border-b h-[38px] border-[var(--ui-border-color)] hover:bg-[var(--secondary-background-color)] last:border-0"
								>
									{columns?.filter(col => !col?.hidden)?.map((column, colIndex) => (
										<td key={colIndex} className="px-4 text-[13px] text-[var(--secondary-text-color)]">
											{column.cellTemplate ? column.cellTemplate(row[column.field], row) : row[column.field]}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
			{isPaginationRequired && totalPagesNum > 0 && totalItemsNum > 10 && (
				<div className="pagination flex items-center gap-[8px] mt-[12px]">
					<p className="page-rows text-[13px] leading-[20px] font-normal text-[#676879]">
						Rows per page
					</p>
					<select
						value={itemsPerPageNum}
						onChange={(e) => handleItemsPerPageChange?.(e.target.value)}
						className="dropdown h-[28px] rounded-[4px] border border-[#C3C6D4] text-[13px] leading-[16px] text-[#676879] px-[8px] font-medium"
					>
						{recordsPerPage.size.map((size) => (
							<option key={size} value={size}>{size}</option>
						))}
					</select>
					<p className="rows-number text-[13px] leading-[20px] font-normal text-[#676879]">
						{`${start}-${end} of ${totalItemsNum}`}
					</p>

					<div className="prev-next flex items-center gap-[16px]">
						<button
							onClick={() => handlePageChange?.(1)}
							disabled={currentPageNum === 1}
							className="text-[#676879] text-[13px] font-normal leading-[16px] disabled:opacity-50"
						>
							<ChevronFirst className="w-4 h-4" />
						</button>

						<button
							onClick={() => handlePageChange?.(Math.max(1, currentPageNum - 1))}
							disabled={currentPageNum === 1}
							className="text-[#676879] text-[13px] font-normal leading-[16px] disabled:opacity-50"
						>
							<ChevronLeft className="w-4 h-4" />
						</button>

						<button
							onClick={() => handlePageChange?.(Math.min(totalPagesNum, currentPageNum + 1))}
							disabled={currentPageNum === totalPagesNum}
							className="text-[#676879] text-[13px] font-normal leading-[16px] disabled:opacity-50"
						>
							<ChevronRight className="w-4 h-4" />
						</button>

						<button
							onClick={() => handlePageChange?.(totalPagesNum)}
							disabled={currentPageNum === totalPagesNum}
							className="text-[#676879] text-[13px] font-normal leading-[16px] disabled:opacity-50"
						>
							<ChevronLast className="w-4 h-4" />
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
